// Roving tab-stop + arrow-key navigation for a horizontal listbox of pills — the shared keyboard model behind
// EnumValuePills and SegmentPills. One button is tabbable at a time (`roving`); Arrow/Home/End move focus with
// wrap-around. The stored index is clamped as the list streams in / resizes, and (optionally) follows an
// externally-driven active index — reconciled at render (store-info-from-previous-renders), not an effect, so
// a focused pill that unmounts mid-stream doesn't leave the tab-stop dangling.

import type { KeyboardEvent } from 'react';
import { useRef, useState } from 'react';

export function useRovingListFocus(count: number, activeIndex = -1) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState(activeIndex >= 0 ? activeIndex : 0);
  const [seen, setSeen] = useState({ active: activeIndex, len: count });
  if (seen.active !== activeIndex || seen.len !== count) {
    setSeen({ active: activeIndex, len: count });
    // Follow the active index when one is provided; otherwise just keep the stored index in range.
    if (activeIndex >= 0) setFocusIndex(activeIndex);
    else if (focusIndex > count - 1) setFocusIndex(Math.max(0, count - 1));
  }
  const roving = count ? Math.min(focusIndex, count - 1) : 0;

  const focusAt = (i: number) => {
    setFocusIndex(i);
    refs.current[i]?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!count) return;
    // Where the user ACTUALLY is, not where the store thinks they are. A pick can now shrink the list
    // (selecting a fold's last unclaimed spelling re-merges the pair), and React keys the chips by value
    // — so the focused node survives at a NEW index while the stored one is only clamped into range. The
    // two then disagree and the next arrow key re-focuses the pill the user is already standing on.
    const here = refs.current.indexOf(e.currentTarget);
    const from = here >= 0 ? here : roving;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt((from + 1) % count);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt((from - 1 + count) % count);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAt(count - 1);
    }
  };

  return { refs, roving, setFocusIndex, onKeyDown };
}
